import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import {
  FiHome,
  FiUsers,
  FiPhone,
  FiClipboard,
  FiClock,
  FiCheck,
  FiX,
  FiSearch,
  FiFilter,
  FiAlertTriangle,
  FiChevronRight,
  FiMessageSquare,
  FiStar,
  FiRefreshCw
} from 'react-icons/fi';

function Rooms() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPreset, setFilterPreset] = useState('all'); // 'all', 'pending', 'occupied', 'has_requests', 'vacant'
  const [sortBy, setSortBy] = useState('room_asc'); // 'room_asc', 'room_desc', 'requests_desc', 'pending_desc', 'guests_desc'
  const [selectedRoom, setSelectedRoom] = useState(null);

  const fetchRooms = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/rooms');
      setRooms(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRooms();
    const interval = setInterval(fetchRooms, 30000);
    return () => clearInterval(interval);
  }, [fetchRooms]);

  // Overall KPI metrics
  const totalGuests = useMemo(() => {
    return rooms.reduce((sum, room) => sum + (room.guests?.length || 0), 0);
  }, [rooms]);

  const totalPending = useMemo(() => {
    return rooms.reduce(
      (sum, room) =>
        sum +
        (room.serviceRequests || []).filter((request) =>
          ['received', 'in-progress', 'pending'].includes(request.status)
        ).length,
      0
    );
  }, [rooms]);

  const totalCompleted = useMemo(() => {
    return rooms.reduce(
      (sum, room) =>
        sum +
        (room.serviceRequests || []).filter((request) => request.status === 'completed').length,
      0
    );
  }, [rooms]);

  // Filtered & Sorted Rooms List
  const processedRooms = useMemo(() => {
    let result = [...rooms];

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((room) => {
        const roomNoMatch = String(room.room_no || '').toLowerCase().includes(q);
        const guestMatch = (room.guests || []).some(
          (g) =>
            String(g.name || '').toLowerCase().includes(q) ||
            String(g.phone || '').toLowerCase().includes(q)
        );
        const requestMatch = (room.serviceRequests || []).some(
          (r) =>
            String(r.category || '').toLowerCase().includes(q) ||
            String(r.details || '').toLowerCase().includes(q)
        );
        return roomNoMatch || guestMatch || requestMatch;
      });
    }

    // Preset filter
    if (filterPreset === 'pending') {
      result = result.filter((room) =>
        (room.serviceRequests || []).some((r) =>
          ['received', 'in-progress', 'pending'].includes(r.status)
        )
      );
    } else if (filterPreset === 'occupied') {
      result = result.filter((room) => (room.guests || []).length > 0);
    } else if (filterPreset === 'has_requests') {
      result = result.filter((room) => (room.serviceRequests || []).length > 0);
    } else if (filterPreset === 'vacant') {
      result = result.filter((room) => (room.guests || []).length === 0);
    }

    // Sort
    result.sort((a, b) => {
      const aRequests = a.serviceRequests || [];
      const bRequests = b.serviceRequests || [];
      const aPending = aRequests.filter((r) => ['received', 'in-progress', 'pending'].includes(r.status)).length;
      const bPending = bRequests.filter((r) => ['received', 'in-progress', 'pending'].includes(r.status)).length;
      const aGuests = (a.guests || []).length;
      const bGuests = (b.guests || []).length;

      if (sortBy === 'requests_desc') return bRequests.length - aRequests.length;
      if (sortBy === 'pending_desc') return bPending - aPending;
      if (sortBy === 'guests_desc') return bGuests - aGuests;
      if (sortBy === 'room_desc') return String(b.room_no).localeCompare(String(a.room_no), undefined, { numeric: true });
      // Default: room_asc
      return String(a.room_no).localeCompare(String(b.room_no), undefined, { numeric: true });
    });

    return result;
  }, [rooms, searchQuery, filterPreset, sortBy]);

  const getStatusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'completed') {
      return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">Completed</span>;
    }
    if (s === 'dnd') {
      return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200">DND</span>;
    }
    if (s === 'critical' || s === 'critical_fix') {
      return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">Critical Fix</span>;
    }
    if (s === 'dispute' || s === 'guest_disputed') {
      return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200">Service Dispute</span>;
    }
    return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">Pending</span>;
  };

  if (loading && rooms.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 px-2 sm:px-3 lg:px-0">
        
        {/* ── Top Header Title ─────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
              Rooms Workspace
            </h1>
            <p className="text-xs text-gray-400 mt-0.5">
              Live room status, registered guests, and service requests across all hotel rooms.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchRooms}
            className="self-start sm:self-auto flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-xs font-semibold shadow-xs transition-all"
          >
            <FiRefreshCw className="h-3.5 w-3.5 text-gray-400" />
            <span>Refresh</span>
          </button>
        </div>

        {/* ── Summary KPI Cards Strip ───────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-xs">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Rooms</p>
            <p className="text-2xl font-extrabold text-gray-900 mt-1">{rooms.length}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Registered rooms</p>
          </div>
          <div className="rounded-2xl border border-teal-100 bg-teal-50/40 p-4 shadow-xs">
            <p className="text-[10px] font-bold text-teal-800 uppercase tracking-wider">Active Guests</p>
            <p className="text-2xl font-extrabold text-teal-700 mt-1">{totalGuests}</p>
            <p className="text-[11px] text-teal-600/80 mt-0.5">Checked in currently</p>
          </div>
          <div className="rounded-2xl border border-amber-100 bg-amber-50/40 p-4 shadow-xs">
            <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider">Pending Load</p>
            <p className="text-2xl font-extrabold text-amber-700 mt-1">{totalPending}</p>
            <p className="text-[11px] text-amber-600/80 mt-0.5">Awaiting staff action</p>
          </div>
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4 shadow-xs">
            <p className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider">Completed</p>
            <p className="text-2xl font-extrabold text-emerald-700 mt-1">{totalCompleted}</p>
            <p className="text-[11px] text-emerald-600/80 mt-0.5">Requests fulfilled</p>
          </div>
        </div>

        {/* ── Preset Filters & Search Controls Strip ───────── */}
        <div className="bg-white rounded-2xl border border-gray-200/80 p-3.5 shadow-xs space-y-3">
          
          {/* Row A: Quick Preset Filters */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <span className="text-xs font-bold text-gray-400 flex items-center gap-1 shrink-0 mr-1">
              <FiFilter className="h-3.5 w-3.5 text-gray-400" /> Presets:
            </span>

            {[
              { id: 'all', label: 'All Rooms', count: rooms.length, bg: 'hover:bg-gray-100 text-gray-700' },
              { id: 'pending', label: 'Pending Requests', count: rooms.filter(r => (r.serviceRequests || []).some(req => ['received', 'in-progress', 'pending'].includes(req.status))).length, bg: 'hover:bg-amber-50 text-amber-800' },
              { id: 'occupied', label: 'Occupied / Guests', count: rooms.filter(r => (r.guests || []).length > 0).length, bg: 'hover:bg-teal-50 text-teal-800' },
              { id: 'has_requests', label: 'Has Requests', count: rooms.filter(r => (r.serviceRequests || []).length > 0).length, bg: 'hover:bg-purple-50 text-purple-800' },
              { id: 'vacant', label: 'Vacant / No Guests', count: rooms.filter(r => (r.guests || []).length === 0).length, bg: 'hover:bg-slate-50 text-slate-700' }
            ].map((p) => {
              const isSelected = filterPreset === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setFilterPreset(p.id)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 shrink-0 ${
                    isSelected
                      ? 'bg-gray-900 text-white shadow-xs'
                      : `bg-gray-100/80 ${p.bg}`
                  }`}
                >
                  <span>{p.label}</span>
                  <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${isSelected ? 'bg-white/20 text-white' : 'bg-black/5 text-gray-600'}`}>
                    {p.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Row B: Search & Sort Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5 pt-1 border-t border-gray-100">
            {/* Search Input */}
            <div className="relative w-full sm:max-w-md">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by Room Number, Guest Name, or Phone..."
                className="w-full pl-9 pr-8 py-2 bg-gray-50/80 hover:bg-white focus:bg-white rounded-xl border border-gray-200 text-xs font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all placeholder-gray-400"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
                >
                  <FiX className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
              <span className="text-xs text-gray-400 font-semibold shrink-0">Sort:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1.5 rounded-xl border border-gray-200 bg-gray-50/80 hover:bg-white text-gray-700 text-xs font-semibold focus:outline-none focus:border-teal-500 cursor-pointer"
              >
                <option value="room_asc">Room No (Ascending)</option>
                <option value="room_desc">Room No (Descending)</option>
                <option value="pending_desc">Most Pending Requests</option>
                <option value="requests_desc">Most Total Requests</option>
                <option value="guests_desc">Most Guests</option>
              </select>
            </div>
          </div>
        </div>

        {/* ── Room Cards Grid View ──────────────────────────── */}
        {processedRooms.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center">
            <FiHome className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-bold text-gray-700 mb-1">No matching rooms found</p>
            <p className="text-xs text-gray-400">Try adjusting your search query or selected preset filter</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {processedRooms.map((room) => {
              const roomRequests = room.serviceRequests || [];
              const roomGuests = room.guests || [];
              const pendingCount = roomRequests.filter((r) =>
                ['received', 'in-progress', 'pending'].includes(r.status)
              ).length;
              const completedCount = roomRequests.filter((r) => r.status === 'completed').length;
              const isOccupied = roomGuests.length > 0;
              const primaryGuest = roomGuests[0] || null;

              return (
                <div
                  key={room.room_no}
                  onClick={() => setSelectedRoom(room)}
                  className={`rounded-2xl bg-white p-4 border transition-all duration-200 cursor-pointer flex flex-col justify-between group shadow-xs hover:shadow-md hover:-translate-y-0.5 ${
                    pendingCount > 0
                      ? 'border-amber-200 hover:border-amber-400'
                      : isOccupied
                      ? 'border-teal-100 hover:border-teal-300'
                      : 'border-gray-100 hover:border-gray-300'
                  }`}
                >
                  {/* Card Header */}
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2.5">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 border ${
                          pendingCount > 0
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : isOccupied
                            ? 'bg-teal-50 text-teal-700 border-teal-200'
                            : 'bg-gray-50 text-gray-600 border-gray-200'
                        }`}>
                          <FiHome className="h-4 w-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-extrabold text-gray-900 group-hover:text-teal-600 transition-colors">
                            Room {room.room_no}
                          </h3>
                          <p className="text-[11px] text-gray-400 font-medium">
                            {isOccupied ? `${roomGuests.length} ${roomGuests.length === 1 ? 'Guest' : 'Guests'}` : 'Vacant'}
                          </p>
                        </div>
                      </div>

                      {/* Status Tag */}
                      {pendingCount > 0 ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-100 text-amber-800 border border-amber-300 animate-pulse">
                          {pendingCount} Pending
                        </span>
                      ) : isOccupied ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-50 text-teal-700 border border-teal-200">
                          Occupied
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500">
                          Idle
                        </span>
                      )}
                    </div>

                    {/* Guest Information Pill */}
                    {primaryGuest ? (
                      <div className="mt-2 p-2 bg-gray-50/70 rounded-xl border border-gray-100 text-xs">
                        <p className="font-bold text-gray-900 truncate">{primaryGuest.name}</p>
                        {primaryGuest.phone && (
                          <p className="text-[11px] text-gray-500 font-mono mt-0.5 flex items-center gap-1">
                            <FiPhone className="h-3 w-3 text-gray-400" />
                            {primaryGuest.phone}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2 p-2 bg-gray-50/40 rounded-xl border border-dashed border-gray-100 text-[11px] text-gray-400 italic text-center">
                        No active guest registered
                      </div>
                    )}
                  </div>

                  {/* Card Footer: Requests Stats & CTA */}
                  <div className="mt-3 pt-2.5 border-t border-gray-100">
                    <div className="flex items-center justify-between text-xs mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 font-medium">Requests:</span>
                        <span className="font-extrabold text-gray-900">{roomRequests.length}</span>
                      </div>
                      <div className="flex items-center gap-1 text-[10px] font-bold">
                        {completedCount > 0 && (
                          <span className="text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                            {completedCount} Done
                          </span>
                        )}
                        {pendingCount > 0 && (
                          <span className="text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-100">
                            {pendingCount} Pend
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-teal-600 font-bold group-hover:underline">
                      <span>View Room Details</span>
                      <FiChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Room Details & Requests Modal ──────────────────── */}
        {selectedRoom && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-xs">
            <div className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl overflow-hidden border border-gray-100 flex flex-col max-h-[90vh]">
              
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white shrink-0">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-teal-50 border border-teal-200 flex items-center justify-center font-bold text-teal-800 text-sm">
                    <FiHome className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
                      Room {selectedRoom.room_no}
                    </h3>
                    <p className="text-xs text-gray-500 font-medium">
                      {(selectedRoom.guests || []).length} Registered Guests · {(selectedRoom.serviceRequests || []).length} Total Requests
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedRoom(null)}
                  className="p-1.5 rounded-full text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                >
                  <FiX size={18} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto space-y-5 flex-1">
                
                {/* 1. Registered Guests in Room */}
                <div>
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                    <FiUsers className="text-gray-400" /> Registered Guests ({(selectedRoom.guests || []).length})
                  </h4>

                  {(selectedRoom.guests || []).length === 0 ? (
                    <div className="p-4 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-center text-xs text-gray-400">
                      No active guests checked into this room currently.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {(selectedRoom.guests || []).map((guest) => (
                        <div
                          key={guest.id}
                          className="p-3.5 bg-gray-50/80 rounded-xl border border-gray-200/80 flex flex-col justify-between"
                        >
                          <div>
                            <p className="font-extrabold text-xs text-gray-900">{guest.name}</p>
                            {guest.phone && (
                              <p className="text-xs text-teal-700 font-mono font-semibold mt-1 flex items-center gap-1">
                                <FiPhone className="h-3 w-3" />
                                <a href={`tel:${guest.phone}`} className="hover:underline">{guest.phone}</a>
                              </p>
                            )}
                          </div>

                          <div className="mt-2.5 pt-2 border-t border-gray-200/60 flex items-center justify-between text-[10px] text-gray-400">
                            {guest.checkin && <span>Check-in: {formatDate(guest.checkin)}</span>}
                            {guest.checkout && <span>Check-out: {formatDate(guest.checkout)}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 2. Service Requests for Room */}
                <div>
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                    <FiClipboard className="text-gray-400" /> Service Requests History ({(selectedRoom.serviceRequests || []).length})
                  </h4>

                  {(selectedRoom.serviceRequests || []).length === 0 ? (
                    <div className="p-4 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-center text-xs text-gray-400">
                      No service requests logged for this room.
                    </div>
                  ) : (
                    <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
                      {(selectedRoom.serviceRequests || []).map((req) => (
                        <div
                          key={req.id}
                          className="p-3.5 rounded-xl border border-gray-200 bg-white hover:border-teal-300 hover:shadow-xs transition-all flex flex-col gap-2"
                        >
                          <div className="flex items-start justify-between gap-3 text-xs">
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded text-[11px] font-mono">
                                  #{String(req.id || '').slice(0, 8)}
                                </span>
                                <span className="font-extrabold text-gray-900">
                                  {req.category}
                                </span>
                                {getStatusBadge(req.status)}
                                {req.guest_rating && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-0.5">
                                    <FiStar className="h-3 w-3 fill-amber-400 text-amber-400" /> {req.guest_rating}
                                  </span>
                                )}
                              </div>

                              {req.details && (
                                <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">
                                  {req.details}
                                </p>
                              )}

                              {req.guest_name && (
                                <p className="text-[11px] text-gray-400 mt-1">
                                  Requested by: <span className="text-gray-700 font-medium">{req.guest_name}</span>
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="pt-2 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400 font-medium">
                            <span>Created: {formatDateTime(req.created_at)}</span>
                            {req.status === 'completed' && req.updated_at && (
                              <span>Completed: {formatDateTime(req.updated_at)}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>

              {/* Modal Footer */}
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => setSelectedRoom(null)}
                  className="px-4 py-1.5 rounded-full text-xs font-bold bg-gray-900 text-white hover:bg-black transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
}

export default Rooms;
